import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Link,
  Typography,
  Button,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { ExeReportHistoryStatus } from "../../components-new/layout/constants";
import { useGetErrorCodesQuery } from "../../useQuery";
import { useNavigate } from "react-router-dom";

const StyledTableCell = styled(TableCell)`
  color: #232d3d;
  font-size: 14px;
  font-weight: 600;
  border-bottom: 1px solid #e2e6f3;
`;

const StyledTableBodyCell = styled(TableCell)`
  padding: 8px 20px;
  border-bottom: 1px solid #eee;
  p {
    color: #2b3539;
    font-size: 16px;
    font-weight: 500;
    padding-right: 15px;
    min-width: 60px;
  }
`;

const typeStyles = {
  PASS: `
    border-radius: 41px;
    border: 0px solid #30ca17;
    background: #e7fbe4;
  `,
  FAIL: `
    border-radius: 41px;
    border: 0px solid #e15151;
    background: #fbe7e7;
  `,
  ABEND: `
    border-radius: 41px;
    background: #eef4fc;
  `,
  ABORTED: `
    border-radius: 41px;
    background: #fbe7e7;
  `,
};

const StatusPill = styled(Chip)(({ type }) => ({
  minWidth: "82px",
  padding: "8px 10px",
  gap: "10px",
  ...(typeStyles[type] && { [typeStyles[type]]: true }),
  "& span": {
    textTransform: "capitalize",
  },
}));

const FilterContainer = styled(Box)`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  align-items: center;
`;

const ExeTestCaseReportsTable = ({ data, isSuccess, testSuiteName, runDate, tsrid }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filteredData, setFilteredData] = useState([]);
  const [errorCodeFilter, setErrorCodeFilter] = useState("");
  const navigate = useNavigate();

  const { data: errorCodesResponse, isSuccess: isErrorCodesSuccess } = useGetErrorCodesQuery(
    tsrid,
    {
      enabled: Boolean(tsrid),
    },
  );

  // Extract error codes from the response
  const errorCodes = errorCodesResponse?.error_codes || errorCodesResponse;

  useEffect(() => {
    if (data) {
      setFilteredData(data);
    }
  }, [data]);

  useEffect(() => {
    if (data) {
      if (errorCodeFilter === "") {
        setFilteredData(data);
      } else {
        const filtered = data.filter((item) => item.issuetype === errorCodeFilter);
        setFilteredData(filtered);
      }
      setPage(0);
    }
  }, [errorCodeFilter, data]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStepHistory = (e, item) => {
    e.preventDefault();
    let url;
    sessionStorage.setItem("logFileFromExeTestCaseReports", item.logfile);
    if (!testSuiteName) {
      url = `/new/reports?tcrid=${item.idtest_case_results}&tcname=${item.testcase}&tcRundate=${item.Executed_Date}`;
    } else {
      url = `/new/reports?tcrid=${item.idtest_case_results}&name=${testSuiteName}&date=${runDate}&tcname=${item.testcase}&tcRundate=${item.Executed_Date}`;
    }
    window.open(url, "_blank");
  };

  const handleDefectAnalysis = (e, item) => {
    e.preventDefault();
    if (item.Status === "FAIL") {
      const url = `/new/defect-analysis?tsrid=${item.idtest_case_results}&tcname=${item.testcase}&tcRundate=${item.Executed_Date}`;
      window.open(url, "_blank");
    }
  };

  const handleErrorCodeFilterChange = (event) => {
    setErrorCodeFilter(event.target.value);
  };

  const handleViewReport = (item) => {
    navigate(`/new/test-case-report?tcrid=${item.idtest_case_results}&tcname=${item.testcase}&tcRundate=${item.Executed_Date}`);
  };

  return (
    <Box flex={1}>
      {isSuccess && (
        <>
          {isErrorCodesSuccess && errorCodes && (
            <FilterContainer>
              <Typography variant="subtitle1">Filter by Error Code:</Typography>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="error-code-filter-label">Error Code</InputLabel>
                <Select
                  labelId="error-code-filter-label"
                  id="error-code-filter"
                  value={errorCodeFilter}
                  label="Error Code"
                  onChange={handleErrorCodeFilterChange}
                >
                  <MenuItem value="">
                    <em>All Error Codes</em>
                  </MenuItem>
                  {Object.entries(errorCodes).map(([code, details]) => (
                    <MenuItem key={code} value={code}>
                      {code} - {details.message} ({details.failedTestCasesCount})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </FilterContainer>
          )}

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Run Date</StyledTableCell>
                  <StyledTableCell>Run By</StyledTableCell>
                  <StyledTableCell>Run Duration</StyledTableCell>
                  <StyledTableCell>Log File</StyledTableCell>
                  <StyledTableCell>Browser</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell>Error Code</StyledTableCell>
                  <StyledTableCell>Actions</StyledTableCell>
                  <StyledTableCell>Failure Analysis</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length ? (
                  filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((item, index) => (
                      <TableRow key={index}>
                        <StyledTableBodyCell>{item.Executed_Date}</StyledTableBodyCell>
                        <StyledTableBodyCell>{item.Executed_By}</StyledTableBodyCell>
                        <StyledTableBodyCell>{item.Duration}</StyledTableBodyCell>
                        <StyledTableBodyCell>
                          <Link target="_blank" href={item.logfile}>
                            {item.testcase}
                          </Link>
                        </StyledTableBodyCell>
                        <StyledTableBodyCell>{item.Browser}</StyledTableBodyCell>
                        <StyledTableBodyCell>
                          <StatusPill
                            label={ExeReportHistoryStatus[item.Status]}
                            type={item.Status}
                          />
                        </StyledTableBodyCell>
                        <StyledTableBodyCell>
                          {item.issuetype && (
                            <Chip
                              size="small"
                              label={item.issuetype}
                              color={item.issuetype === "E0000" ? "error" : "warning"}
                            />
                          )}
                        </StyledTableBodyCell>
                        <StyledTableBodyCell>
                          <Box display="flex" gap={1} flexDirection="row">
                            {item.Status !== "ABORTED" && (
                              <Link
                                target="_blank"
                                sx={{ cursor: "pointer" }}
                                onClick={(e) => handleStepHistory(e, item)}
                              >
                                View test results
                              </Link>
                            )}
                        
                              <Link
                                target="_blank"
                                sx={{ cursor: "pointer" }}
                                onClick={() => handleViewReport(item)}
                              >
                                View Report
                              </Link>
                          </Box>
                        </StyledTableBodyCell>
                        <StyledTableBodyCell>
                          <Link
                            sx={{
                              cursor: item.Status === "FAIL" ? "pointer" : "not-allowed",
                              color: item.Status === "FAIL" ? "#1976d2" : "grey",
                              pointerEvents: item.Status === "FAIL" ? "auto" : "none",
                              textDecoration: item.Status === "FAIL" ? "underline" : "none",
                            }}
                            onClick={(e) => item.Status === "FAIL" && handleDefectAnalysis(e, item)}
                          >
                            View Failure Analysis
                          </Link>
                        </StyledTableBodyCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <StyledTableBodyCell align="center" colSpan={9}>
                      No data available
                    </StyledTableBodyCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default ExeTestCaseReportsTable;
